import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { api, fmtError } from "../lib/api";
import { Container, PageHero } from "../components/site/Common";
import { contactItems, socialLinks } from "../components/site/contactInfo";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const { settings: s } = useSite();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!s) return <div className="min-h-screen" />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setF(EMPTY);
      setSent(true);
      toast.success("Message sent. TDBA will get back to you.");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };
  const info = contactItems(s);
  const socials = socialLinks(s);
  const input = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <>
      <PageHero eyebrow="Contact" title="Get in touch with TDBA" text="Questions about coaching, events or the association? Send us a message." />
      <section className="py-16 bg-slate-50">
        <Container className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-4">
            <div className="card p-7">
              <h2 className="font-display text-xl font-bold text-[#0B1F44]">{s.association_name}</h2>
              <div className="mt-6 space-y-5">
                {info.map(([Icon, l, v, href]) => (
                  <div key={l} className="flex gap-4" data-testid={`contact-info-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center shrink-0"><Icon className="h-5 w-5" /></span>
                    <div className="min-w-0"><p className="text-xs text-slate-500">{l}</p>
                      {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="font-medium text-[#0B1F44] hover:text-blue-700 break-all">{v}</a> : <p className="font-medium text-[#0B1F44] break-words whitespace-pre-line">{v}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {socials.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100 flex gap-3">
                  {socials.map(([u, Icon, k]) => <a key={k} href={u} target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full bg-slate-100 text-[#0B1F44] hover:bg-blue-600 hover:text-white transition-colors" data-testid={`contact-social-${k}`}><Icon className="h-4 w-4" /></a>)}
                </div>
              )}
            </div>
            <div className="card overflow-hidden h-72">
              <iframe title="TDBA location" className="w-full h-full" loading="lazy" src={s.map_embed || `https://maps.google.com/maps?q=${encodeURIComponent(s.map_query)}&z=13&output=embed`} referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-testid="contact-map" />
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-3 card p-7 md:p-10 space-y-5" data-testid="contact-form">
            <h2 className="font-display text-2xl font-bold text-[#0B1F44]">Send a message</h2>
            {sent && <p className="rounded-xl bg-emerald-50 text-emerald-700 p-4 text-sm" data-testid="contact-success">Thank you — your message has been received.</p>}
            <div className="grid gap-5 md:grid-cols-2">
              <input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="contact-name-input" />
              <input required type="email" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="contact-email-input" />
              <input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={input} data-testid="contact-phone-input" />
              <input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={input} data-testid="contact-subject-input" />
            </div>
            <textarea required rows={6} placeholder="Your message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={input} data-testid="contact-message-input" />
            <button disabled={busy} className="btn-primary" data-testid="contact-submit-btn">{busy ? "Sending..." : "Send message"} <Send className="h-4 w-4" /></button>
          </form>
        </Container>
      </section>
    </>
  );
}
